import type {ProductFull} from "@/api/product";
import {getSiteUrl} from "@/helpers/functions";
import {coerceImageObjects, type ImageInput} from "@/helpers/imageObject";

export interface SeoSchemaProductProps {
    product: ProductFull;
    pageUrl: string;
    images: ImageInput[];
    description: string;
    isRelatedTo?: string[];
    gtin?: string;
    mpn?: string;
    model?: string;
    additionalProperties?: Array<{name: string; value: string}>;
    isVariantOf?: string;
    isAccessoryOrSparePartFor?: string[];
}

const toAbsoluteUrl = (siteUrl: string, url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${siteUrl}${path}`;
};

export function SeoSchemaProduct({product, pageUrl, images, description, isRelatedTo, gtin, mpn, model, additionalProperties, isVariantOf, isAccessoryOrSparePartFor}: SeoSchemaProductProps) {
    const name = product.title?.trim() ?? "";
    const trimmedDescription = description?.trim() ?? "";

    if (!name)
    {
        console.error(`[SeoSchemaProduct] HALT: Product "${product.slug}" is missing a title. Populate the product's title in the CMS. Skipping Product JSON-LD.`);
        return null;
    }
    if (!trimmedDescription)
    {
        console.error(`[SeoSchemaProduct] HALT: Product "${product.slug}" is missing a description. Populate seoDescription (or shortDescription) in the CMS. Skipping Product JSON-LD.`);
        return null;
    }

    const siteUrl = getSiteUrl();
    const imageNodes = coerceImageObjects(images);
    const absPageUrl = toAbsoluteUrl(siteUrl, pageUrl);
    const related = (isRelatedTo ?? []).map(u => toAbsoluteUrl(siteUrl, u));

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": name,
        "image": imageNodes,
        "description": trimmedDescription,
        "brand": {"@type": "Brand", "name": "ACTIVE"},
        "manufacturer": {"@id": `${siteUrl}/#organization`},
        "url": absPageUrl,
        "sku": product.id,
        "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Licensed cannabis brands",
        },
        "offers": {
            "@type": "Offer",
            "url": absPageUrl,
            "availability": "https://schema.org/InStock",
            "businessFunction": "https://schema.org/Sell",
            "eligibleCustomerType": "http://purl.org/goodrelations/v1#Business",
            "priceCurrency": "USD",
            "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "USD",
                "valueAddedTaxIncluded": false,
            },
            "seller": {"@id": `${siteUrl}/#organization`},
        },
        "isRelatedTo": related,
    };

    const categoryLabel = product.category?.label?.trim();
    if (categoryLabel)
    {
        schema.category = categoryLabel;
    }

    if (gtin) schema.gtin = gtin;
    if (mpn) schema.mpn = mpn;
    if (model) schema.model = model;
    if (additionalProperties && additionalProperties.length > 0) {
        schema.additionalProperty = additionalProperties.map((p) => ({
            "@type": "PropertyValue",
            "name": p.name,
            "value": p.value,
        }));
    }
    if (isVariantOf) {
        schema.isVariantOf = {"@id": toAbsoluteUrl(siteUrl, isVariantOf)};
    }
    if (isAccessoryOrSparePartFor && isAccessoryOrSparePartFor.length > 0) {
        schema.isAccessoryOrSparePartFor = isAccessoryOrSparePartFor.map((u) => ({"@id": toAbsoluteUrl(siteUrl, u)}));
    }

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
